import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Mail, Loader, ArrowRight, RefreshCw } from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import toast from 'react-hot-toast';
import Logo from '../../components/Logo/Logo';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const [isResending, setIsResending] = useState(false);
  const navigate = useNavigate();
  const api = useApi();

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link. No token found.');
      return;
    }

    verifyEmail(token);
  }, [searchParams]);

  const verifyEmail = async (token) => {
    try {
      setStatus('verifying');
      setMessage('Verifying your email address...');

      const response = await api.post('/api/v1/auth/verify-email', { token });

      if (response.data.success) {
        setStatus('success');
        setMessage(response.data.message || 'Email verified successfully! Your billing account has been activated.');
        
        // Show success toast
        toast.success('Email verified successfully! 🎉');

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setStatus('error');
        setMessage(response.data.message || 'Email verification failed. Please try again.');
        toast.error('Verification failed');
      }
    } catch (error) {
      console.error('Email verification error:', error);
      setStatus('error');
      
      if (error.response?.data?.message) {
        setMessage(error.response.data.message);
      } else if (error.message === 'Invalid or expired verification token') {
        setMessage('This verification link has expired or is invalid. Please request a new one.');
      } else {
        setMessage('An error occurred during verification. Please try again.');
      }
      
      toast.error('Verification failed');
    }
  };

  const handleResendVerification = async () => {
    try {
      setIsResending(true);
      
      // Note: This requires the user to be logged in
      // You might need to implement a different flow for non-logged-in users
      const response = await api.post('/api/v1/auth/resend-verification');
      
      if (response.data.success) {
        toast.success('Verification email sent! Please check your inbox.');
        setMessage('A new verification email has been sent to your inbox. Please check your email.');
      } else {
        toast.error(response.data.message || 'Failed to resend verification email');
      }
    } catch (error) {
      console.error('Resend verification error:', error);
      toast.error('Failed to resend verification email. Please login and try again.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <Logo className="h-12 w-auto mx-auto" />
          </Link>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          
          {/* Status Icon Section */}
          <div className={`p-8 text-center ${
            status === 'verifying' 
              ? 'bg-gradient-to-br from-blue-500 to-purple-600' 
              : status === 'success'
              ? 'bg-gradient-to-br from-green-500 to-emerald-600'
              : 'bg-gradient-to-br from-red-500 to-pink-600'
          }`}>
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full mb-4">
              {status === 'verifying' && (
                <Loader className="w-10 h-10 text-white animate-spin" />
              )}
              {status === 'success' && (
                <CheckCircle className="w-10 h-10 text-white" />
              )}
              {status === 'error' && (
                <XCircle className="w-10 h-10 text-white" />
              )}
            </div>
            
            <h1 className="text-3xl font-bold text-white mb-2">
              {status === 'verifying' && 'Verifying...'}
              {status === 'success' && 'Verified!'}
              {status === 'error' && 'Verification Failed'}
            </h1>
            
            <p className="text-white/90 text-sm">
              {status === 'verifying' && 'Please wait while we verify your email'}
              {status === 'success' && 'Your email has been successfully verified'}
              {status === 'error' && 'There was a problem verifying your email'}
            </p>
          </div>

          {/* Content Section */}
          <div className="p-8">
            {/* Message Box */}
            <div className={`p-4 rounded-lg mb-6 ${
              status === 'verifying' 
                ? 'bg-blue-50 border border-blue-200' 
                : status === 'success'
                ? 'bg-green-50 border border-green-200'
                : 'bg-red-50 border border-red-200'
            }`}>
              <p className={`text-sm leading-relaxed ${
                status === 'verifying' 
                  ? 'text-blue-800' 
                  : status === 'success'
                  ? 'text-green-800'
                  : 'text-red-800'
              }`}>
                {message}
              </p>
            </div>

            {/* Success State - Benefits */}
            {status === 'success' && (
              <div className="mb-6 space-y-3">
                <h3 className="font-semibold text-gray-900 text-sm mb-3">
                  ✨ What's activated:
                </h3>
                <div className="space-y-2">
                  {[
                    'Your billing account is now active',
                    'Access to all Growth Plan features',
                    'Ability to invite team members',
                    'Full productivity tracking capabilities'
                  ].map((benefit, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>
                
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <p className="text-sm text-gray-600 text-center mb-4">
                    Redirecting to login in 3 seconds...
                  </p>
                  <Link
                    to="/login"
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    Continue to Login
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            )}

            {/* Error State - Actions */}
            {status === 'error' && (
              <div className="space-y-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="font-semibold text-yellow-800 text-sm mb-2">
                    💡 What you can do:
                  </h4>
                  <ul className="text-sm text-yellow-700 space-y-1 list-disc list-inside">
                    <li>Request a new verification email</li>
                    <li>Check if you've already verified</li>
                    <li>Contact support if issues persist</li>
                  </ul>
                </div>

                <div className="grid gap-3">
                  <button
                    onClick={handleResendVerification}
                    disabled={isResending}
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isResending ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4" />
                        Resend Verification Email
                      </>
                    )}
                  </button>

                  <Link
                    to="/login"
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-all duration-200"
                  >
                    Go to Login
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            )}

            {/* Verifying State */}
            {status === 'verifying' && (
              <div className="space-y-4">
                <div className="flex justify-center">
                  <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div className="bg-gradient-to-r from-blue-600 to-purple-600 h-full rounded-full animate-pulse w-3/4"></div>
                  </div>
                </div>
                <p className="text-center text-sm text-gray-500">
                  This should only take a moment...
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-8 py-6 bg-gray-50 border-t border-gray-100">
            <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
              <Mail className="w-4 h-4" />
              <span>
                Need help?{' '}
                <a 
                  href="mailto:support@teamgrid.com" 
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  Contact support
                </a>
              </span>
            </div>
          </div>
        </div>

        {/* Bottom Links */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Don't have an account?{' '}
            <Link to="/register" className="text-blue-600 hover:text-blue-700 font-semibold">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;

